import { type IdPrefix, isId } from "shared/Ids";
import {
  CURRENT_ORGANISATION_REF,
  CURRENT_PROJECT_REF,
  organisationPagePath,
  projectPagePath,
} from "shared/Permalinks";

// Portal URLs carry their scope as the ID of what they show: `/portal/proj_…/…` for a project's pages
// (its organisation is the project's) and `/portal/org_…/…` for an organisation's. IDs never change,
// so an address survives renames and moves. Account pages (`/portal/profile`, …) have none.
//
// Either also takes `proj_current` / `org_current` (the visitor's last project or organisation), for
// links made where the ID isn't known (menus, the docs); the portal redirects those to the canonical
// address.

export { CURRENT_ORGANISATION_REF, CURRENT_PROJECT_REF };

/** Account pages: under `/portal`, outside any organisation or project. */
export const ACCOUNT_SECTIONS = ["profile", "support", "design-system"] as const;

export type PortalLevel = "project" | "organisation";

export type ParsedPortalPath =
  | { kind: "root" }
  | { kind: "account"; rest: string }
  | { kind: "organisation"; orgRef: string; rest: string }
  | { kind: "project"; projectRef: string; rest: string }
  | { kind: "other" };

const joinRest = (segments: string[]) => (segments.length ? `/${segments.join("/")}` : "");

const PROJECT_PREFIX: IdPrefix = "proj";
const ORGANISATION_PREFIX: IdPrefix = "org";

/**
 * What a pathname points at. `rest` is the page within its scope, such as `/api-keys` or
 * `/assets/<id>` for a project, `/billing` for an organisation and `""` for its first page.
 * `projectRef` / `orgRef` is the ID in the address as written (possibly malformed) or the
 * current-scope stand-in.
 */
export const parsePortalPath = (pathname: string): ParsedPortalPath => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "portal") return { kind: "other" };
  const [, first, ...rest] = segments;
  if (!first) return { kind: "root" };
  if ((ACCOUNT_SECTIONS as readonly string[]).includes(first))
    return { kind: "account", rest: joinRest(segments.slice(1)) };
  const ref = decodeURIComponent(first);
  if (ref.startsWith(`${PROJECT_PREFIX}_`))
    return { kind: "project", projectRef: ref, rest: joinRest(rest) };
  if (ref.startsWith(`${ORGANISATION_PREFIX}_`))
    return { kind: "organisation", orgRef: ref, rest: joinRest(rest) };
  return { kind: "other" };
};

/**
 * Whether a project or organisation reference in an address can name one: a well-formed ID of its
 * kind, or the current-scope stand-in. Anything else is not found.
 */
export const isPortalScopeRef = (ref: string, level: PortalLevel): boolean =>
  level === "project"
    ? ref === CURRENT_PROJECT_REF || isId(ref, PROJECT_PREFIX)
    : ref === CURRENT_ORGANISATION_REF || isId(ref, ORGANISATION_PREFIX);

/** A page of an organisation, by its ID or `org_current`, such as `organisationPath(org.id, "/billing")`. */
export const organisationPath = (orgRef: string, rest = "") => organisationPagePath(orgRef, rest);
/** A page of a project, by its ID or `proj_current`, such as `projectPath(project.id, "/settings")`. */
export const projectPath = (projectRef: string, rest = "") => projectPagePath(projectRef, rest);

/** A page of the current project (`/portal/proj_current/…`), for links made outside it. */
export const currentProjectPath = (rest = "") => projectPath(CURRENT_PROJECT_REF, rest);
/** A page of the current organisation (`/portal/org_current/…`). */
export const currentOrganisationPath = (rest = "") =>
  organisationPath(CURRENT_ORGANISATION_REF, rest);

const startsWithScope = (href: string, prefix: string) =>
  href === prefix || (href.startsWith(prefix) && /^[/?#]/.test(href.slice(prefix.length)));

/**
 * `href` with a link to the current project (`currentProjectPath`) turned into its page in the
 * project of `pathname`, the page it's on, so it needs no redirect; likewise for the current
 * organisation, which on a project page is `orgId` (the organisation the portal has open).
 * Elsewhere it stays as it is, and the portal resolves it.
 */
export const resolveCurrentScopePath = (href: string, pathname: string, orgId?: string): string => {
  const projectPrefix = currentProjectPath();
  const organisationPrefix = currentOrganisationPath();
  const isProject = startsWithScope(href, projectPrefix);
  const isOrganisation = startsWithScope(href, organisationPrefix);
  if (!isProject && !isOrganisation) return href;
  const here = parsePortalPath(pathname);
  const suffix = href.slice((isProject ? projectPrefix : organisationPrefix).length);
  if (isProject && here.kind === "project" && here.projectRef !== CURRENT_PROJECT_REF)
    return projectPath(here.projectRef, suffix);
  if (isOrganisation && here.kind === "organisation" && here.orgRef !== CURRENT_ORGANISATION_REF)
    return organisationPath(here.orgRef, suffix);
  if (isOrganisation && here.kind === "project" && orgId) return organisationPath(orgId, suffix);
  return href;
};

/**
 * Where switching to another project (or organisation) from a page goes: the same page there, or
 * its section when the page shows one resource the other doesn't have (an asset, a lens, a hash
 * list, a policy). The query, which can name such resources (a folder), is dropped.
 */
export const switchedPortalRest = (rest: string): string => {
  const segments = rest.split("/").filter(Boolean);
  if (segments[0] === "assets") return "";
  if (segments.length <= 1 || rest === "/lenses/new") return rest;
  return `/${segments[0]}`;
};

/**
 * The organisation or project a portal page names; none for the current-scope stand-ins, malformed
 * IDs (their pages are not found), account pages and others.
 */
export interface PortalScopeQuery {
  orgId?: string;
  projectId?: string;
}

/** The scope in a portal pathname; none for account pages, the bare portal and other routes. */
export const portalScopeOf = (pathname: string): PortalScopeQuery => {
  const parsed = parsePortalPath(pathname);
  if (parsed.kind === "project" && isId(parsed.projectRef, PROJECT_PREFIX))
    return { projectId: parsed.projectRef };
  if (parsed.kind === "organisation" && isId(parsed.orgRef, ORGANISATION_PREFIX))
    return { orgId: parsed.orgRef };
  return {};
};

/** A scope as a string, `""` for none, to tell whether a page is in another scope. */
export const portalScopeKey = (scope: PortalScopeQuery) => scope.projectId ?? scope.orgId ?? "";

/**
 * The canonical address of the page a portal pathname names, for the scope the portal resolved:
 * `/portal/proj_current/x` becomes `/portal/<project ID>/x`. Null when it already is canonical (or
 * isn't a scoped page).
 */
export const canonicalPortalPath = (
  pathname: string,
  scope: { orgId: string; projectId: string | null },
): string | null => {
  const parsed = parsePortalPath(pathname);
  if (parsed.kind === "project" && scope.projectId)
    return parsed.projectRef === scope.projectId ? null : projectPath(scope.projectId, parsed.rest);
  if (parsed.kind === "organisation")
    return parsed.orgRef === scope.orgId ? null : organisationPath(scope.orgId, parsed.rest);
  return null;
};
